import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { type EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import CircularProgress from '@mui/material/CircularProgress';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Trans, useTranslation } from 'react-i18next';
import { drawDefinitionConstants } from 'tods-competition-factory';

import AlertIcon from 'assets/icons/icn _ 24px _ alert.svg?react';
import TMXAlert from 'components/alert/TMXAlert';
import StandardButton from 'components/buttons/StandardButton';
import TMXDialog from 'components/dialogs/TMXDialog';
import { H3 } from 'components/typography/Typography';
import { useTournamentService } from 'hooks/tournament/useTournamentService';
import { type CloudResult } from 'repository/repository';
import { type EventCost } from 'typedefs/event';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';

import PromoteToMainDrawCosts from './PromoteToMainDrawCosts';
import { useStyles } from './styles';
import { type EventCostResult, getIsTeam, getPromoteParticipantsData, handleSetEventCostResult } from './utils';

const { QUALIFYING } = drawDefinitionConstants;

export interface PromoteToMainDrawDialogProps {
  addToTeamParticipants?: ParticipantInterface[] | [];
  eventsIds: string[];
  eventType?: EventTypeEnum;
  isOpen?: boolean;
  loading?: boolean;
  participantEventCostData?: EventCost[];
  promoteParticipants: ParticipantInterface[];
  entryStage?: 'MAIN' | 'QUALIFYING';
  tournamentId: string;
  closeDialog?: () => void;
  onAddToTeam?: (playerIds: string[], teamId: string) => Promise<void>;
  onPromotePlayers?: () => Promise<void>;
  onPromoteTeamPlayers?: (participantIds: string[]) => Promise<void>;
  onResetRowSelection?: () => void;
}

const PromoteToMainDrawDialog: React.FC<React.PropsWithChildren<PromoteToMainDrawDialogProps>> = ({
  addToTeamParticipants,
  eventsIds,
  eventType,
  isOpen,
  loading,
  participantEventCostData,
  promoteParticipants,
  entryStage,
  tournamentId,
  closeDialog,
  onAddToTeam,
  onPromotePlayers,
  onPromoteTeamPlayers,
  onResetRowSelection,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const tournamentService = useTournamentService();
  const [isSaving, setIsSaving] = useState(loading);
  const paperProps = { style: { padding: 30, minWidth: 580 } };
  const isAddToTeam = !!addToTeamParticipants;

  const promoteParticipantsData = useMemo(
    () =>
      getPromoteParticipantsData({
        addToTeamParticipants,
        eventType,
        promoteParticipants,
      }),
    [addToTeamParticipants, eventType, promoteParticipants],
  );

  const isTeam = getIsTeam(promoteParticipantsData);

  const allPromoteParticipantIds = useMemo(
    () => promoteParticipantsData?.map((p) => p?.participantId),
    [promoteParticipantsData],
  );

  const allPromoteParticipantPersonIds = useMemo(
    () => promoteParticipantsData?.flatMap((p) => p?.personIds),
    [promoteParticipantsData],
  );

  const [{ eventCostData, costLoading, error }, setEventCostResult] = useState<EventCostResult>({
    eventCostData: undefined,
  });

  const handleGetEventCostResult = useCallback(
    async () =>
      allPromoteParticipantPersonIds?.length
        ? await tournamentService.getEventsCosts(
            eventsIds,
            allPromoteParticipantPersonIds,
            tournamentId,
            'network-only',
          )
        : ({ eventCostData: [] } as CloudResult<EventCost[]>),
    [allPromoteParticipantPersonIds, eventsIds, tournamentId, tournamentService],
  );

  const handleOnClickPromotePlayers = useCallback(async () => {
    setIsSaving(true);
    if (onPromoteTeamPlayers || isAddToTeam) {
      onPromoteTeamPlayers
        ? await onPromoteTeamPlayers(allPromoteParticipantIds)
        : promoteParticipantsData?.map(async (p) => await onAddToTeam(p?.personIds, p.participantId));
    } else {
      await onPromotePlayers();
    }
    setIsSaving(false);
    onResetRowSelection?.();
    closeDialog?.();
  }, [
    allPromoteParticipantIds,
    closeDialog,
    isAddToTeam,
    promoteParticipantsData,
    onAddToTeam,
    onPromoteTeamPlayers,
    onPromotePlayers,
    onResetRowSelection,
  ]);

  const dialogContent: {
    title: string;
    message:
      | 'confirmMovePlayersToTeamsInfo'
      | 'confirmMoveTeamsToQualifyingInfo'
      | 'confirmMovePlayersToQualifyingInfo'
      | 'confirmMoveTeamsInfo'
      | 'confirmMovePlayersInfo'
      | 'confirmMovePlayersToSelectedInfo';
    confirmText: string;
  } = useMemo(() => {
    switch (true) {
      case isAddToTeam:
        return {
          title: t(
            'teams.addWithCostTitle',
            promoteParticipantsData?.length === 1 ? { teamName: promoteParticipantsData?.[0]?.name } : null,
          ),
          message: 'confirmMovePlayersToTeamsInfo',
          confirmText: t('teams.addToTeam'),
        };
      case entryStage === QUALIFYING:
        return {
          title: isTeam ? t('moveToQualifyingTeams') : t('moveToQualifyingPlayers'),
          message: isTeam ? 'confirmMoveTeamsToQualifyingInfo' : 'confirmMovePlayersToQualifyingInfo',
          confirmText: t('confirmMove'),
        };
      case isTeam:
        return {
          title: t('moveTeamsToSelected'),
          message: 'confirmMoveTeamsInfo',
          confirmText: t('confirmMove'),
        };
      case !!onPromoteTeamPlayers:
        return {
          title: t('movePlayersToAvaialble'),
          message: 'confirmMovePlayersInfo',
          confirmText: t('confirmMove'),
        };
      default:
        return {
          title: t('moveToSelected'),
          message: 'confirmMovePlayersToSelectedInfo',
          confirmText: t('confirmMove'),
        };
    }
  }, [isAddToTeam, t, promoteParticipantsData, entryStage, isTeam, onPromoteTeamPlayers]);

  useEffect(() => {
    handleSetEventCostResult(
      participantEventCostData,
      handleGetEventCostResult,
      setEventCostResult,
      allPromoteParticipantIds,
    );
  }, [allPromoteParticipantIds, handleGetEventCostResult, participantEventCostData]);

  return (
    <TMXDialog open={isOpen || loading} PaperProps={paperProps} onClose={closeDialog}>
      <DialogTitle id="replace-alternate-dialog-title">
        <Grid container>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <H3>{dialogContent.title}</H3>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        {costLoading ||
        (!eventCostData && allPromoteParticipantPersonIds?.length > 0) ||
        !promoteParticipantsData?.length ? (
          <CircularProgress className={classes.loading} />
        ) : (
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography>
                <Trans
                  components={{
                    strong: <strong />,
                  }}
                  i18nKey={dialogContent.message}
                />
              </Typography>
            </Grid>
            {eventCostData?.length && allPromoteParticipantPersonIds?.length ? (
              <>
                <TMXAlert className={classes.warningBox} icon={true} iconSvg={<AlertIcon />}>
                  <Typography>
                    <Trans components={{ strong: <strong /> }} i18nKey={'chargesWillApplay'} />
                  </Typography>
                </TMXAlert>

                <Grid item xs={12}>
                  <PromoteToMainDrawCosts eventCosts={eventCostData} participantsToPromote={promoteParticipantsData} />
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <Typography>{t('noAdditionalCharges')}</Typography>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Grid container direction="row" justifyContent="flex-end" spacing={2}>
          <Grid item>
            <StandardButton onClick={closeDialog}>{t('ccl')}</StandardButton>
          </Grid>
          <Grid item>
            <StandardButton
              disabled={costLoading || (error && error?.message !== '')}
              loading={isSaving || loading}
              variant="primary"
              onClick={handleOnClickPromotePlayers}
            >
              {dialogContent.confirmText}
            </StandardButton>
          </Grid>
        </Grid>
      </DialogActions>
    </TMXDialog>
  );
};

export default PromoteToMainDrawDialog;
